import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { HttpStatusCode } from '../../shared/constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../../shared/instrumentation/instrumented-http.service';
import { FileType } from '../../shared/models/file-item.model';
import { HttpRequestOptions } from '../../shared/models/http-request-options.model';
import { OperationStream } from '../../shared/models/operation-stream.model';
import { MruBaseService } from '../../shared/mru/mru-base.service';
import { MruConfig, MruConfigService } from '../../shared/mru/mru-config.service';
import { AccessTokenService } from '../../shared/services/access-token.service';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { SchedulerService } from '../../shared/services/scheduler.service';
import { UpdateContentService } from '../../shared/services/update-content.service';
import { XhrServiceError } from '../../shared/services/xhr.service';
import { ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { ObservableHelper } from '../../shared/utilities/observable-helper.utility';
import { SharedServiceData, SharedWithMeResponse } from './shared-with-me.model';

/**
 * Service that retrieves shared MRU documents from MRU++ web API.
 */
@Injectable({
    providedIn: 'root'
})
export class SharedWithMeService extends MruBaseService {
    private static readonly sharingSpecifier = '&sharingTypes=ShareFlow'; // Currently modern attachments are not being sent as part of the SwM response from what I can tell, and we will need additional logic to handle it when it does
    private readonly sharedDocsDataStreamMap: Map<string, OperationStream<SharedServiceData>>;
    private readonly mruConfig: MruConfig;
    private readonly sharingTypeParam: string;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        mruConfigService: MruConfigService,
        platformLocation: PlatformLocation,
        globalErrorHandler: OfficeHomePageErrorHandler,
        startupConfigService: ClientStartupConfigService,
        private readonly updateContentService: UpdateContentService,
        scheduler: SchedulerService
    ) {
        super(accessTokenService,
            instrumentationService,
            instrumentedHttp,
            contextService,
            officeHomeApiConfigService,
            mruConfigService,
            platformLocation,
            globalErrorHandler,
            startupConfigService,
            mruConfigService.config.sharedWithMeV2Enabled ? 'docs/v2.0/sharedwithme' : 'docs/sharedwithme',
            mruConfigService.config.supportedFileTypesSwM,
            scheduler);

        this.mruConfig = mruConfigService.config;
        this.sharingTypeParam = this.mruConfig.sharedWithMeV2Enabled && !this.mruConfig.sharedAttachmentsEnabled ? SharedWithMeService.sharingSpecifier : '';
        this.sharedDocsDataStreamMap = new Map<string, OperationStream<SharedServiceData>>();
    }

    public getSharedDocuments(count: number, types?: Set<FileType>): OperationStream<SharedServiceData> {
        //TODO: Skip new requests if sufficient data of desired types is present in cache
        const url = `${this.serviceBaseUrl}?${this.requestParams(count, types)}${this.sharingTypeParam}`;

        if (this.sharedDocsDataStreamMap.has(url)) {
            return this.sharedDocsDataStreamMap.get(url);
        }

        const updateStream = this.updateContentService.getOrAddUpdateStream(`sharedwithme-${url}`, () => this.fetchSharedData(url, FetchStrategy.NetworkFirst));
        const sharedDataFromCache = this.fetchSharedData(url, FetchStrategy.CacheOnly);
        const sharedDataFromNetwork = this.fetchSharedData(url, FetchStrategy.NetworkFirst);
        const sharedDataStream = ObservableHelper.createResultsWithCacheObservable(
            sharedDataFromCache,
            sharedDataFromNetwork,
            updateStream,
            _ => this.sharedDocsDataStreamMap.delete(url)
        );

        this.sharedDocsDataStreamMap.set(url, sharedDataStream);

        return sharedDataStream;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mocksharedwithme' && this.mockRequestHeader) {
            requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private fetchSharedData(serviceUrl: string, strategy: FetchStrategy): Promise<SharedServiceData> {
        return new Promise<SharedServiceData>((resolve, reject) => {
            const version = this.mruConfig.sharedWithMeV2Enabled ? '2' : '1';
            const promiseCallback = (promise: InstrumentedHttpPromise<SharedWithMeResponse>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);

                        if (result.ok) {
                            params[InstrumentationParam.SharedWithMeVersion] = version;
                            if (result.data && result.data.shared_documents) {
                                const itemsCount = result.data.shared_documents.length;
                                params[InstrumentationParam.ActualSize] = itemsCount.toString();
                            } else {
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                        }
                    })
                    .then(result => {
                        if (result.ok && result.data && result.data.shared_documents) {
                            result.data.shared_documents.sort((a, b) => new Date(b.SharedDate).getTime() - new Date(a.SharedDate).getTime());
                            resolve({
                                items: result.data.shared_documents,
                                fromCache: result.fromCache
                            });
                        } else {
                            reject(new XhrServiceError(result, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.sMMaG);
                    });
            };

            this.get<SharedWithMeResponse>(
                KnownInstrumentationEvents.SharedWithMeRequest,
                KnownRequestInstrumentationEvents.SharedWithMeRequest,
                serviceUrl,
                promiseCallback,
                reject,
                this.getNewRequestConfig(false),
                strategy);
        });
    }
}
